import CodeOutlined from '@ant-design/icons/CodeOutlined';
import FileTextOutlined from '@ant-design/icons/FileTextOutlined';
import type { FormInstance } from 'antd/es/form';
import Tabs from 'antd/es/tabs';
import 'codemirror/addon/edit/closebrackets';
import 'codemirror/addon/edit/matchbrackets';
import 'codemirror/addon/fold/brace-fold';
import 'codemirror/addon/fold/foldcode';
import 'codemirror/addon/fold/foldgutter';
import 'codemirror/addon/fold/foldgutter.css';
import 'codemirror/addon/lint/json-lint';
import 'codemirror/addon/lint/lint';
import 'codemirror/addon/lint/lint.css';
import 'codemirror/lib/codemirror.css';
import 'codemirror/mode/javascript/javascript';
import 'codemirror/theme/material-ocean.css';
import React, { useEffect, useState } from 'react';
import { Controlled as CodeMirror } from 'react-codemirror2';
import styled from 'styled-components';

import type { JSONSchemaType } from '@mai/types';

import { PropertyManager } from './PropertyManager';
import { SchemaBasics } from './SchemaBasics';
import { SchemaPreview } from './SchemaPreview';
import { SchemaValidation } from './SchemaValidation';

const Container = styled.div`
  ${({ theme }) => `
  height: 100%;
  display: flex;
  flex-direction: column;
  background: ${theme.colorBgContainer};
`}
`;

const EditorTabs = styled(Tabs)`
  ${({ theme }) => `
  height: 100%;
  
  .ant-tabs-nav {
    margin-bottom: 0;
    padding: 0 ${theme.padding}px;
    background: ${theme.colorBgLayout};
    border-bottom: ${theme.lineWidth}px ${theme.lineType} ${theme.colorBorderSecondary};
  }
  
  .ant-tabs-content {
    height: calc(100% - ${theme.controlHeightLG + 6}px);
    overflow: hidden;
  }
  
  .ant-tabs-tabpane {
    height: 100%;
  }
`}
`;

const TabContent = styled.div`
  ${({ theme }) => `
  height: 100%;
  display: flex;
  gap: ${theme.marginLG}px;
  padding: ${theme.paddingLG}px;
  overflow: hidden;
`}
`;

const LeftPanel = styled.div`
  ${({ theme }) => `
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: ${theme.marginLG}px;
  overflow-y: auto;
  padding-right: ${theme.paddingMD}px;
`}
`;

const RightPanel = styled.div`
  ${({ theme }) => `
  flex: 0 0 ${theme.screenXSMax - 175}px;
  display: flex;
  flex-direction: column;
  gap: ${theme.marginLG}px;
  overflow: hidden;
`}
`;

const CodeEditorWrapper = styled.div`
  ${({ theme }) => `
  height: 100%;
  border: ${theme.lineWidth}px ${theme.lineType} ${theme.colorBorder};
  border-radius: ${theme.borderRadius}px;
  overflow: hidden;
  background: ${theme.colorBgTextActive};
  
  .CodeMirror {
    height: 100%;
    font-size: ${theme.fontSizeSM}px;
    font-family: ${theme.fontFamilyCode};
  }
  
  .CodeMirror-gutters {
    background: ${theme.colorBgTextActive};
    border-right: ${theme.lineWidth}px solid ${theme.colorBorder};
  }
`}
`;

interface JsonSchemaEditorProps {
  value?: JSONSchemaType;
  onChange?: (value: JSONSchemaType) => void;
  form?: FormInstance;
  name?: string | string[];
}

export const JsonSchemaEditor: React.FC<JsonSchemaEditorProps> = ({
  value = {
    type: 'object',
    properties: {},
    required: [],
  },
  onChange,
  form,
  name,
}) => {
  const [schema, setSchema] = useState(value);
  const [jsonValue, setJsonValue] = useState(JSON.stringify(value, null, 2));
  const [activeTab, setActiveTab] = useState('visual');

  // Update internal state when value prop changes
  useEffect(() => {
    if (value && JSON.stringify(value) !== JSON.stringify(schema)) {
      setSchema(value);
      setJsonValue(JSON.stringify(value, null, 2));
    }
  }, [value, schema]);

  const handleSchemaChange = (newSchema: JSONSchemaType) => {
    setSchema(newSchema);
    setJsonValue(JSON.stringify(newSchema, null, 2));

    if (onChange) {
      onChange(newSchema);
    }

    if (form && name) {
      form.setFieldValue(name, newSchema);
    }
  };

  const handleJsonChange = (
    _editor: unknown,
    _data: unknown,
    value: string,
  ) => {
    setJsonValue(value);

    try {
      const parsed = JSON.parse(value);
      setSchema(parsed);

      if (onChange) {
        onChange(parsed);
      }

      if (form && name) {
        form.setFieldValue(name, parsed);
      }
    } catch (e) {
      // Invalid JSON - don't update schema
    }
  };

  const addProperty = (
    propertyName: string,
    propertySchema: JSONSchemaType,
  ) => {
    const newSchema = {
      ...schema,
      properties: {
        ...schema.properties,
        [propertyName]: propertySchema,
      },
    };
    handleSchemaChange(newSchema);
  };

  const updateProperty = (
    propertyName: string,
    propertySchema: JSONSchemaType,
  ) => {
    const newSchema = {
      ...schema,
      properties: {
        ...schema.properties,
        [propertyName]: propertySchema,
      },
    };
    handleSchemaChange(newSchema);
  };

  const deleteProperty = (propertyName: string) => {
    const { [propertyName]: _, ...remainingProperties } =
      schema.properties || {};
    const newRequired = (schema.required || []).filter(
      (r: string) => r !== propertyName,
    );

    const newSchema = {
      ...schema,
      properties: remainingProperties,
      required: newRequired,
    };
    handleSchemaChange(newSchema);
  };

  const toggleRequired = (propertyName: string) => {
    const required = schema.required || [];
    const newRequired = required.includes(propertyName)
      ? required.filter((r: string) => r !== propertyName)
      : [...required, propertyName];

    const newSchema = {
      ...schema,
      required: newRequired,
    };
    handleSchemaChange(newSchema);
  };

  const updateBasics = (updates: Partial<typeof schema>) => {
    handleSchemaChange({
      ...schema,
      ...updates,
    });
  };

  return (
    <Container>
      <EditorTabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          {
            key: 'visual',
            label: (
              <>
                <FileTextOutlined /> Visual Editor
              </>
            ),
            children: (
              <TabContent>
                <LeftPanel>
                  <SchemaBasics schema={schema} onUpdate={updateBasics} />
                  <PropertyManager
                    properties={schema.properties || {}}
                    required={schema.required || []}
                    onAdd={addProperty}
                    onUpdate={updateProperty}
                    onDelete={deleteProperty}
                    onToggleRequired={toggleRequired}
                  />
                </LeftPanel>
                <RightPanel>
                  <SchemaPreview schema={schema} />
                  <SchemaValidation schema={schema} />
                </RightPanel>
              </TabContent>
            ),
          },
          {
            key: 'json',
            label: (
              <>
                <CodeOutlined /> JSON Editor
              </>
            ),
            children: (
              <TabContent>
                <CodeEditorWrapper style={{ flex: 1 }}>
                  <CodeMirror
                    value={jsonValue}
                    options={{
                      mode: { name: 'javascript', json: true },
                      theme: 'material-ocean',
                      lineNumbers: true,
                      lineWrapping: false,
                      matchBrackets: true,
                      autoCloseBrackets: true,
                      foldGutter: true,
                      gutters: [
                        'CodeMirror-linenumbers',
                        'CodeMirror-foldgutter',
                      ],
                      lint: true,
                    }}
                    onBeforeChange={handleJsonChange}
                  />
                </CodeEditorWrapper>
                <RightPanel>
                  <SchemaPreview schema={schema} />
                  <SchemaValidation schema={schema} />
                </RightPanel>
              </TabContent>
            ),
          },
        ]}
      />
    </Container>
  );
};
